/**
 * When the loading toast + overlay should appear for a request.
 *
 * Why
 *   Most lookups finish in well under a second, and showing "처리 중입니다..." for each
 *   one made the screen flash (e.g. closing a popup re-queries the list). A read that
 *   finishes within READ_LOADER_DELAY_MS now shows nothing.
 *
 *   Writes keep the loader IMMEDIATELY. The overlay (Toast.jsx, z-index 20002) is what
 *   stops a second click on Save while the first is in flight; delaying it would open a
 *   300ms window for that second click. So only requests that are clearly reads are
 *   delayed, and anything unknown is treated as a write.
 *
 * What counts as a read
 *   - not a saveRequest/deleteRequest (no reqType) and no confirm dialog, and
 *   - the method name (tran: process key, URL: last path segment) starts with a read
 *     verb, or is a ...Paging list call.
 *   request() is also used for writes (createRAG, deleteRAGData, embedding, ...), so
 *   "no reqType" alone is not enough; the name has to say it is a read.
 */
export const READ_LOADER_DELAY_MS = 300;

const READ_NAME = /^(select|search|get|find|fetch|list|check)/i;
const PAGING_NAME = /Paging$/;

const requestName = (url, options) => {
  if (url) return String(url).split("?")[0].split("/").filter(Boolean).pop() ?? "";
  return Object.keys(options?.process?.[0] ?? {})[0] ?? "";
};

export const isReadRequest = (url, options = {}, messageOptions = {}) => {
  if (messageOptions.reqType || messageOptions.showConfirm) return false;
  const name = requestName(url, options);
  return READ_NAME.test(name) || PAGING_NAME.test(name);
};

/**
 * Runs show() now (delayMs <= 0) or after delayMs. cancel() stops a pending show;
 * `shown` tells the caller whether there is a loader to close.
 */
export const deferLoader = (show, delayMs) => {
  let shown = false;
  const run = () => {
    shown = true;
    show();
  };
  let timer = null;
  if (delayMs > 0) timer = setTimeout(run, delayMs);
  else run();
  return {
    get shown() {
      return shown;
    },
    cancel: () => clearTimeout(timer),
  };
};
